/**
 * The four states every list page renders around its rows — loading, nothing-created-yet, nothing-matched,
 * and failed-to-load. Shared so Workflows / Dashboards / Context / Plans / Chats read identically.
 */
import { AlertTriangle, type LucideIcon, SearchX } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import type { ViewMode } from '@/_shared/lib/list-controls';
import { cn } from '@/_shared/lib/utils';

/**
 * Placeholder rows/cards matching the restored view, so a list-view user never sees cards snap to a table.
 *
 * `maxWidthClass` must be the SAME width the caller gives its real content region. It is a prop rather
 * than a constant because the width is not shared: four list pages sit at `max-w-5xl` and Activations at
 * `max-w-7xl`, and with the value hardcoded here Activations drew its skeleton 16rem narrower than the
 * table that replaced it — so every load of that page ended in a visible sideways snap. A full Tailwind
 * class, not a size token, because the JIT only sees literal strings.
 */
export function ListSkeleton({
  view,
  className,
  maxWidthClass = 'max-w-5xl',
}: {
  view: ViewMode;
  className?: string;
  maxWidthClass?: string;
}): React.ReactElement {
  return (
    <div className={cn('min-h-0 flex-1 overflow-hidden', className)}>
      <div className={cn('mx-auto px-6 py-6', maxWidthClass)}>
        {view === 'list' ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The shared shell all three non-loading states are drawn in: a centered `Empty` block with an icon
 * medallion, a title, a line of explanation, and an optional action.
 *
 * `fill` covers the two slots these render in — `ListEmpty` / `ListError` replace the whole content
 * region (a flex sibling of the page header, hence `flex-1`), while `ListNoMatches` sits inside the
 * already-sized `min-h-0 flex-1` row container (hence `h-full`).
 */
function ListState({
  icon: Icon,
  iconClassName,
  title,
  description,
  fill = 'flex-1',
  children,
}: {
  icon: LucideIcon;
  iconClassName?: string;
  title: React.ReactNode;
  description: React.ReactNode;
  fill?: 'flex-1' | 'h-full';
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn('flex items-center justify-center p-6', fill)}>
      <Empty className="max-w-md">
        <EmptyHeader>
          <EmptyMedia variant="icon" className={iconClassName}>
            <Icon />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
        {children && <EmptyContent>{children}</EmptyContent>}
      </Empty>
    </div>
  );
}

/** "Nothing here yet" — the list has never had an item. `children` carries the create CTA. */
export function ListEmpty({
  icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <ListState icon={icon} title={title} description={description}>
      {children}
    </ListState>
  );
}

/** The list has items, but the current search/filters match none of them. */
export function ListNoMatches({
  noun,
  onClear,
}: {
  /** Plural noun for the copy, e.g. `'dashboards'`. */
  noun: string;
  onClear: () => void;
}): React.ReactElement {
  return (
    <ListState
      icon={SearchX}
      fill="h-full"
      title={`No matching ${noun}`}
      description={`No ${noun} match your search and filters. Try adjusting them.`}
    >
      <Button variant="secondary" onClick={onClear}>
        Clear search &amp; filters
      </Button>
    </ListState>
  );
}

/**
 * The list query failed with nothing cached to fall back on. Same shell as the other two states, with
 * the icon medallion tinted danger so the failure reads as one at a glance.
 */
export function ListError({
  title,
  message,
  children,
}: {
  title: string;
  message: string;
  /** Optional recovery action (e.g. a Retry button). */
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <ListState icon={AlertTriangle} iconClassName="bg-danger-bg text-danger" title={title} description={message}>
      {children}
    </ListState>
  );
}
